import React, { useMemo, ReactNode } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';

// Components
import Breadcrumb from '../../container/breadcrumb';
import HeaderActions from '../../container/header_actions';
import PeriodBreadcrumb from '../../component/PeriodBreadcrumb';
import ConditionalRender from '../../helpers/ConditionalRender';
import FilterBar from '../../component/FilterBar';
import PageContent from '../../container/page_content';
import { OverflowTabbedMenu, getOrderedTabs } from '../../container/overflow-tabbed-menu';
import { useTabLanding } from '../../hooks/useTabLanding';

// Types
import HostTarget from '../../types/host/HostTarget';

// Constants
import { DATA_LOADING } from '../../component/Constants';

export const HOST_TAB_ORDER_KEY = 'host-detail';

export const hostNavItems = (hostId?: string) => [
    {
        label: 'Host Metrics',
        icon: 'fal fa-server',
        path: `/hosts/${hostId}`,
        orderKey: 'metrics',
        show: true,
        end: true,
    },
    {
        label: 'Database Activity',
        icon: 'fal fa-database',
        path: `/hosts/${hostId}/instances`,
        orderKey: 'instances',
        show: true,
        end: false,
    },
    {
        label: 'Events',
        icon: 'fal fa-exchange',
        path: `/hosts/${hostId}/events`,
        orderKey: 'events',
        show: true,
        end: false,
    },
];

interface HostsTabbedProps {
    hosts: HostTarget[];
    toggleMenu: Function;
    setFilterOptions?: Function;
    refresh: () => void;
    loading?: number;
    activeTab: 'metrics' | 'instances' | 'events';
    title: string;
    description: string;
    children: ReactNode;
}

const HostsTabbed: React.FC<HostsTabbedProps> = ({
    hosts,
    toggleMenu,
    setFilterOptions,
    refresh,
    loading,
    activeTab,
    title,
    description,
    children
}) => {
    const { hostId } = useParams();
    const host = hosts.find(item => item.id === Number(hostId));

    const navItems = useMemo(() => hostNavItems(hostId), [hostId]);

    // Opening a host lands on the first tab the user arranged.
    useTabLanding({
        recordPath: hostId ? `/hosts/${hostId}` : undefined,
        landingPath: hostId ? `/hosts/${hostId}` : undefined,
        resolveTab: () => getOrderedTabs(navItems, HOST_TAB_ORDER_KEY)[0]?.path,
    });

    return (
        <PageContent title={title} description={description}>
            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading={host?.name || ''}
                            type="hosts"
                            hostId={Number(hostId)}>
                    <Link to="/hosts">Analysis</Link>
                    <Link to="/hosts">Hosts</Link>
                    <Link to={`/hosts/${hostId}`}>{host?.name}</Link>
                    <NavLink to={`/hosts/${hostId}`} className={"current-breadcrumb-link"}>
                        {activeTab === 'metrics' && 'Host metrics'}
                        {activeTab === 'instances' && 'Database activity'}
                        {activeTab === 'events' && 'Events'}
                    </NavLink>
                    <PeriodBreadcrumb />
                </Breadcrumb>
                <HeaderActions 
                    toggleMenu={toggleMenu} 
                    refresh={refresh}
                    
                    setFilterOptions={setFilterOptions}
                />
            </div>

            <div className="loader">
                <ConditionalRender if={loading === DATA_LOADING}>
                    <div className="bar"/>
                </ConditionalRender>
            </div>

            {/* Filters */}
            <ConditionalRender if={setFilterOptions}>
                {setFilterOptions && <FilterBar setFilterOptions={setFilterOptions}/>}
            </ConditionalRender>

            {/* Tab Navigation */}
            <OverflowTabbedMenu items={navItems} orderStorageKey={HOST_TAB_ORDER_KEY}/>

            {/* Page Content */}
            {children}
        </PageContent>
    );
};

export default HostsTabbed;
